/** Finding a message, and getting to it.
 *
 *  All of it runs on the client, over the payload already in memory. Measured
 *  against 19,000 synthesized rows: 0.33-0.92 ms for a naive scan, 0.13-0.54 ms
 *  against the folded haystack below. A frame is 16.7 ms, so search runs on
 *  every keystroke and there is nothing to debounce and no endpoint to add.
 *
 *  The whole file exists to protect ONE invariant: an offset computed against a
 *  folded string must still be correct against the original. Break it and the
 *  highlight lands beside the word, or halfway inside an emoji.
 */
import { F, localMonth, type Row } from "./types";
import { monthLabel } from "./rows";

/** How many results are DRAWN. Not how many are found -- the arrows walk the
 *  whole array. 'love' matched 4,262 of 19,000 rows in the measurement, and
 *  rendering 4,262 cards is the only slow thing search can do. */
export const RESULT_CAP = 100;

/** One-to-one only. Anything that changes a string's length belongs nowhere
 *  near this map: NFD would decompose an accent into two code units and every
 *  offset after it would point one character to the left. */
const PUNCT: Record<string, string> = {
  "\u2018": "'", "\u2019": "'", "\u201B": "'", "\u02BC": "'",  // curly and modifier apostrophes
  "\u201C": '"', "\u201D": '"', "\u201E": '"',                 // curly double quotes
  "\u2013": "-", "\u2014": "-", "\u2212": "-",                 // en dash, em dash, minus sign
  "\u00A0": " ", "\u2007": " ", "\u2009": " ", "\u202F": " ", // the non-breaking spaces
};

/** Case- and punctuation-folded, and EXACTLY as long as it started.
 *
 *  `.toLowerCase()` is not length-preserving: 'İ' lowercases to two code units,
 *  i + a combining dot, so 'İstanbul ß straße DON’T 🔥 Привет' is 33 characters
 *  and its lowercase is 34. Every offset after the İ would be off by one.
 *
 *  Walking UTF-16 code units and keeping the original whenever the lowercase is
 *  not also one unit makes length preservation true by construction rather than
 *  by care. Each half of an emoji's surrogate pair lowercases to itself, so
 *  emoji pass through whole.
 *
 *  The punctuation half is not cosmetic either: an 88-row sample of this
 *  archive contains twelve U+2019 apostrophes, so without it every "don't" you
 *  type misses every "don’t" Snapchat actually exported.
 */
export function fold(s: string): string {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    const mapped = PUNCT[c];
    if (mapped !== undefined) { out += mapped; continue; }
    const lower = c.toLowerCase();
    out += lower.length === 1 ? lower : c;
  }
  return out;
}

/** The folded haystack, built once per payload.
 *
 *  Keyed on the rows array itself, which is fetched once and never replaced.
 *  The pass costs 18 ms over 572 k characters (~28 ms at full-export scale) --
 *  a dropped frame, so SearchSheet warms it when the sheet opens and the hitch
 *  lands under the opening animation instead of under the first keystroke.
 */
const haystacks = new WeakMap<Row[], (string | null)[]>();

export function foldedBodies(rows: Row[]): (string | null)[] {
  let folded = haystacks.get(rows);
  if (!folded) {
    // The body, and only the body. Never placeholder(): an unsaved row RENDERS
    // "unsaved -- not kept by Snapchat", and searching what is rendered would
    // make "snapchat" return every deleted message in the archive.
    folded = rows.map((r) => (r[F.Body] ? fold(r[F.Body]) : null));
    haystacks.set(rows, folded);
  }
  return folded;
}

/** One match per MESSAGE, carrying every occurrence inside it.
 *
 *  Per message rather than per occurrence because the bar's arrows move you a
 *  message at a time: two hits in one bubble are one place on screen, and
 *  pressing the arrow twice to go nowhere reads as a broken button.
 */
export interface Match {
  index: number;
  /** [start, end) into the ORIGINAL body -- see fold(). */
  ranges: [number, number][];
}

export function findMatches(rows: Row[], query: string): Match[] {
  const q = fold(query).trim();
  if (!q) return [];   // not everything: a blank query lighting up 19,000 rows looks like a hang

  const hay = foldedBodies(rows);
  const out: Match[] = [];
  for (let i = 0; i < hay.length; i++) {
    const body = hay[i];
    if (body === null) continue;
    let at = body.indexOf(q);
    if (at < 0) continue;
    const ranges: [number, number][] = [];
    while (at >= 0) {
      ranges.push([at, at + q.length]);
      at = body.indexOf(q, at + q.length);
    }
    out.push({ index: i, ranges });
  }
  return out;
}

// ------------------------------------------------------------------- months
export interface Month {
  ym: string;      // 'YYYY-MM'
  label: string;   // 'Jun 2024'
  count: number;
  index: number;   // the month's FIRST row
}

/** Every month the archive actually has messages in, in timeline order.
 *
 *  Which is the argument against an <input type="month">: you cannot pick May
 *  2023, land nowhere, and be left wondering whether the app is broken or the
 *  month is empty. The payload is ordered by ts_local, so a month is one
 *  contiguous run and a single pass is enough.
 */
export function monthsOf(rows: Row[]): Month[] {
  const out: Month[] = [];
  let open: Month | null = null;
  for (let i = 0; i < rows.length; i++) {
    const ym = localMonth(rows[i]);
    if (open === null || open.ym !== ym) {
      open = { ym, label: monthLabel(ym), count: 0, index: i };
      out.push(open);
    }
    open.count++;
  }
  return out;
}

/** 'jun', '2024' and 'jun 2024' all reach June 2024. Folded the same way
 *  messages are, so the two lists in the sheet never disagree about case. */
export function matchMonths(months: Month[], query: string): Month[] {
  const q = fold(query).trim();
  if (!q) return [];
  return months.filter((m) => fold(m.label).includes(q));
}

// ---------------------------------------------------------------- highlight
export interface Segment { text: string; hit: boolean }

/** Alternating plain and matched runs, which ALWAYS rejoin to the original
 *  string. That invariant is what makes a wrong offset impossible to ship: no
 *  arrangement of ranges can drop, duplicate or reorder what you were sent.
 */
export function splitHighlight(
  text: string,
  ranges: readonly (readonly [number, number])[],
): Segment[] {
  const clipped = ranges
    .map(([a, b]) => [
      Math.max(0, Math.min(a, text.length)),
      Math.max(0, Math.min(b, text.length)),
    ] as [number, number])
    .filter(([a, b]) => b > a)
    .sort((x, y) => x[0] - y[0]);

  if (clipped.length === 0) return [{ text, hit: false }];

  // Overlapping and touching ranges merge, or the same characters would be
  // emitted twice and the reader would see a doubled word.
  const merged: [number, number][] = [clipped[0]];
  for (let i = 1; i < clipped.length; i++) {
    const [a, b] = clipped[i];
    const last = merged[merged.length - 1];
    if (a <= last[1]) last[1] = Math.max(last[1], b);
    else merged.push([a, b]);
  }

  const segments: Segment[] = [];
  let at = 0;
  for (const [a, b] of merged) {
    if (a > at) segments.push({ text: text.slice(at, a), hit: false });
    segments.push({ text: text.slice(a, b), hit: true });
    at = b;
  }
  if (at < text.length) segments.push({ text: text.slice(at), hit: false });
  return segments;
}

/** A window of a long message, centred on its first match.
 *
 *  The window MOVES the text, so every range has to move with it -- the same
 *  offset invariant fold() protects, one layer up and exactly as easy to get
 *  silently wrong. Ranges that fall outside are dropped rather than clamped: a
 *  mark stretched to the edge of a snippet would claim a match that is not
 *  visible in it.
 */
export interface Snippet {
  text: string;
  ranges: [number, number][];
  /** True when this is a window rather than the whole message, so the card can
   *  say so instead of implying that was all that was said. */
  clipped: boolean;
}

export function snippetAround(
  text: string,
  ranges: readonly (readonly [number, number])[],
  radius: number,
): Snippet {
  const width = radius * 2;
  if (text.length <= width) {
    return { text, ranges: ranges.map(([a, b]) => [a, b] as [number, number]), clipped: false };
  }

  const anchor = ranges.length > 0 ? ranges[0][0] : 0;
  const to = Math.min(text.length, Math.max(0, anchor - radius) + width);
  const from = Math.max(0, to - width);   // re-derived, so a match near the end still fills the window

  return {
    text: text.slice(from, to),
    ranges: ranges
      .filter(([a, b]) => a >= from && b <= to)
      .map(([a, b]) => [a - from, b - from] as [number, number]),
    clipped: from > 0 || to < text.length,
  };
}
